import { isRegistrationChangeByAthletePeriodEnded } from '~/mixins/championship'

const getDefaultState = () => ({
  summary: null,
  registrationRequests: {},
  fetching: false,
  valid: false,
  currentRegistrationId: null
})

export const state = () => getDefaultState()

export const getters = {
  summary(state) {
    return state.summary
  },
  canEdit(state) {
    if (!state.currentRegistrationId) return false
    if (!state.summary) return false

    const registration = state.summary.registrations.find(
      r => r.id === +state.currentRegistrationId
    )

    return (
      ['pendent', 'completed'].includes(registration.status) &&
      !isRegistrationChangeByAthletePeriodEnded(
        registration.championship.registrationChangeByAthletePeriodEnd,
        registration.championship.timeZone
      )
    )
  }
}

export const mutations = {
  summary(state, summary) {
    state.summary = summary
  },
  setRegistrationRequest(state, { registrationId, request }) {
    state.registrationRequests = {
      ...state.registrationRequests,
      [registrationId]: request
    }
  },
  setFetching(state, fetching) {
    state.fetching = fetching
  },
  setCurrentRegistrationId(state, id) {
    state.currentRegistrationId = id
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
  valid(state, valid) {
    state.valid = valid
  }
}

export const actions = {
  async fetchRegistration({ commit, dispatch, state }, registrationId) {
    // Guard: prevent duplicate simultaneous calls
    if (state.fetching) {
      return
    }

    // Cache: if data is already loaded for this ID, don't reload
    if (
      state.currentRegistrationId === registrationId &&
      state.summary &&
      state.registrationRequests
    ) {
      return
    }

    commit('setFetching', true)

    try {
      // Validate the registration before fetching data, this endpoint
      // will also update pricing information if needed.
      await dispatch('validateRegistration', registrationId)

      if (state.valid) {
        // Fetch both summary and request data using await to ensure registration summary
        // is loaded before the request data.
        await dispatch('fetchRegistrationSummary', registrationId)

        const registrations = state.summary?.registrations || []
        await Promise.all(registrations.map(r => dispatch('fetchRegistrationRequest', r.id)))

        commit('setCurrentRegistrationId', registrationId)
      } else {
        throw new Error('Invalid registration')
      }
    } finally {
      commit('setFetching', false)
    }
  },
  async validateRegistration({ commit }, registrationId) {
    await this.$coreApi
      .$post(`/athletes/championship_registrations/${registrationId}/validate_registration`)
      .then(_response => commit('valid', true))
      .catch(error => {
        commit('valid', false)
        throw error
      })
  },
  async fetchRegistrationSummary({ commit }, registrationId) {
    // Clean up previous data
    commit('summary', null)

    await this.$coreApi
      .$get(`/athletes/championship_registrations/${registrationId}`)
      .then(response => {
        commit('summary', response.data)
      })
      .catch(error => {
        throw error
      })
  },
  async fetchRegistrationRequest({ commit }, registrationId) {
    if (registrationId) {
      await this.$nodeApi
        .get(`admin/championships/championshipsRegistrations/${registrationId}/request`)
        .then(response => {
          commit('setRegistrationRequest', {
            registrationId,
            request: response.data
          })
        })
        .catch(error => {
          if (error.response?.status === 404) {
            commit('setRegistrationRequest', {
              registrationId,
              request: null
            })
          } else {
            throw error
          }
        })
    }
  }
}
